import { useDaycareStore, type DaycareSlot } from "~/stores/useDaycareStore";
import {
  useInventoryStore,
  MAX_LEVEL,
  MAX_STARS,
} from "~/stores/useInventoryStore";
import { pickInventoryCandidateForQueueEntry } from "~/utils/daycare";
import type { DaycareQueueEntry } from "~/utils/daycare";
import type { Rarity } from "~/data/gacha";

type HatchLoopSlot = {
  slug: string;
  nameFr: string;
  nameEn: string;
  rarity: string | Rarity;
  isShiny: boolean;
  stars: number;
  cosmeticFormSlug?: string | null;
  loopSameSpecies?: boolean;
};

function queueEntryFromSlot(slot: HatchLoopSlot): DaycareQueueEntry {
  return {
    slug: slot.slug,
    nameFr: slot.nameFr,
    nameEn: slot.nameEn,
    rarity: slot.rarity as Rarity,
    isShiny: slot.isShiny,
    stars: slot.stars,
    cosmeticFormSlug: slot.cosmeticFormSlug ?? null,
  };
}

/**
 * Après éclosion : pour chaque slot qui avait « boucle même espèce », tente un redépôt niv.100 (hors équipe).
 * Indépendant des toggles auto globaux — contrôle fin par slot.
 */
export function applyPerSlotLoopRefillsFromHatch(
  hatched: { slot: HatchLoopSlot; isShiny: boolean }[],
  maxSlots: number,
): number {
  const daycare = useDaycareStore();
  const inventory = useInventoryStore();
  let deposited = 0;
  for (const { slot } of hatched) {
    if (slot.loopSameSpecies === false) continue;
    if (daycare.slots.length >= maxSlots) continue;
    const entry = queueEntryFromSlot(slot);
    const candidate = pickInventoryCandidateForQueueEntry(
      inventory.collection,
      daycare.slots,
      entry,
      MAX_LEVEL,
      MAX_STARS,
      new Set<number>(),
      true,
    );
    if (!candidate) continue;
    const ok = daycare.deposit(
      {
        slug: candidate.slug,
        nameFr: candidate.nameFr,
        nameEn: candidate.nameEn,
        stars: candidate.stars,
        rarity: candidate.rarity,
        isShiny: candidate.isShiny,
        cosmeticFormSlug: candidate.cosmeticFormSlug ?? null,
        loopSameSpecies: true,
      },
      maxSlots,
    );
    if (ok) deposited++;
  }
  return deposited;
}
